// Relative imports do not inherit Shopify's asset_url version. Bump this version
// whenever product-dpo.js changes so the CDN cannot serve an older module API.
import { connectDpo, restoreDpoQuantity } from './product-dpo.js?v=20260929-quantity';

const { Core, Events, Cache } = Global;
const { $loading, $JSON } = Global.Utils;

const CACHE_KEY = 'variant-provider';

// TODO: Make it as global util -> nodeListToMap
function elementsToMap(elements) {
    return elements.reduce((acc, element) => {
        if (element.id) {
            acc[element.id] = element.cloneNode(true);
        }
        return acc;
    }, {});
}

customElements.define('product-variant-provider', class extends Core {
    pendingRequests = new Map();
    propTypes = {
        'product-page': Boolean,
        'product-url': String,
        'initial-variant-id': String,
        'initial-image-position': Number,
        'initial-selling-plan': String
    }

    elements = {
        $: ['id-input', 'product-meta', 'variants-data'],
        listeners: ['product-variant-listener']
    }

    render() {
        this.variantsData = $JSON(this.$('variants-data'));
        this.variantsDataEntries = Object.entries(this.variantsData);
        this._loadLocalFragments();
        this.sub(Events.VARIANT_CHANGE, this._onVariantChange);
        this.sub(Events.SELLING_PLAN_CHANGE, this._onSellingPlanChange)

        this._handleNoscript();
        // An early DPO app block may already have moved quantity out of its
        // listener. Restore it before caching, including for a switch back here.
        restoreDpoQuantity(this);
        this._initDefaultVariant();
        this._syncOptionControls();
        this.dpo = connectDpo(this);
        if (this.prop('product-page')) this._setupPrefetch();
    }

    _handleNoscript() {
        if (this.$('id-input')) this.$('id-input').disabled = false;
    }

    async _initDefaultVariant() {
        this.variantId = this.prop('initial-variant-id');
        this.variantImagePosition = this.prop('initial-image-position');
        this.sellingPlanId = this.prop('initial-selling-plan');
        this._initCache();
    }

    _onVariantChange({ optionValue, optionIndex, variantId }) {
        let currentVariantId = variantId;
        const index = Number(optionIndex);
        if (optionValue !== undefined && Number.isInteger(index) && optionIndex !== undefined) {
            // Remember explicit shopper choices, not forced fallback options.
            // A missing 29-inch colour may temporarily select 27-inch, but a
            // subsequent colour should return to the preferred 29-inch size.
            const preferred = [...(this.preferredOptions || this.variantsData[this.variantId]?.options || [])];
            preferred[index] = optionValue;
            const candidates = this.variantsDataEntries.filter(this._matchOption({ index, value: optionValue }));
            const exact = candidates.find(([, variant]) => variant.options.every((value, i) => value === preferred[i]));
            // Existence and stock are different: keep an exact sold-out variant
            // selected so Shopify can show Sold Out; never silently resize it.
            const fallbackPool = candidates.some(([, variant]) => variant.available === true)
                ? candidates.filter(([, variant]) => variant.available === true) : candidates;
            const score = entry => entry[1].options.reduce((sum, value, i) =>
                sum + (i !== index && value === preferred[i] ? 1 : 0), 0);
            const stockRank = entry => !entry[1].available ? 0 : entry[1].backorder ? 1 : 2;
            const fallback = fallbackPool.reduce((best, entry) =>
                !best || score(entry) > score(best) ||
                (score(entry) === score(best) && stockRank(entry) > stockRank(best)) ? entry : best, null);
            currentVariantId = (exact || fallback)?.[0];
            if (currentVariantId) this.preferredOptions = preferred;
        } else if (this.variantsData[variantId]) {
            this.preferredOptions = [...this.variantsData[variantId].options];
        }
        if (!this.variantsData[currentVariantId]) return;
        this._setCurrentVariant(currentVariantId);
        this._handleUpdate();
    }

    _matchOption(src) {
        return ([variantId, { options }]) => {
            return options[src.index] === src.value;
        }
    }

    _setCurrentVariant(variantId) {
        this.variantId = variantId;
        this.variantImagePosition = this.variantsData[variantId].imagePosition;
        this.$('id-input').value = variantId;
        this._syncOptionControls();
        // Switch the existing gallery independently of the HTML round trip.
        if (this.variantImagePosition > 0) {
            this._warmImage(variantId);
            this.pub(Events.PRODUCT_GALLERY_SLIDE_CHANGE, this.variantImagePosition - 1);
        }
    }

    _syncOptionControls() {
        const options = this.variantsData[this.variantId].options;
        this.querySelectorAll('product-variant-selector [data-option-index]').forEach(control => {
            const index = Number(control.dataset.optionIndex);
            const value = options[index];
            if (control.hasAttribute?.('data-display-value')) {
                control.textContent = value;
            } else if (control.matches('input[type="radio"]')) {
                const optionValue = control.value ?? control.dataset.optionValue;
                control.checked = optionValue === value;
                const state = this._optionState(index, optionValue);
                const label = control.closest('label');
                if (label) {
                    label.hidden = !state.exists;
                    label.toggleAttribute('data-variant-option-hidden', !state.exists);
                }
                control.disabled = !state.exists;
                control.classList.toggle('!unavailable', !state.exists);
                control.classList.toggle('!sold-out', state.exists && state.soldOut);
                control.classList.toggle('!backorder', state.backorder === true);
                control.dataset.stockState = !state.exists ? 'invalid' : state.soldOut ? 'sold-out' : state.backorder ? 'backorder' : 'in-stock';
                if (label) label.dataset.stockState = control.dataset.stockState;
                this._stockLabel(control, label, control.dataset.stockState);
            } else if (control.matches('select')) {
                control.value = value;
                Array.from(control.options).forEach(option => {
                    const state = this._optionState(index, option.value);
                    option.hidden = !state.exists;
                    option.disabled = !state.exists;
                    option.dataset.stockState = !state.exists ? 'invalid' : state.soldOut ? 'sold-out' : state.backorder ? 'backorder' : 'in-stock';
                    option.textContent = option.value;
                    option.setAttribute('aria-label', option.value + this._stockSuffix(option.dataset.stockState));
                });
            }
        });
        this.querySelectorAll('product-options-dropdown').forEach(dropdown => {
            const select = dropdown.querySelector('select[data-option-index]');
            const label = dropdown.querySelector('[data-element="current"]');
            if (select && label) label.textContent = select.value;
        });
        this.querySelectorAll('product-variant-selector input[data-variant-id]:not([data-option-index])').forEach(control => {
            control.checked = control.dataset.variantId === String(this.variantId);
            const variant = this.variantsData[control.dataset.variantId];
            const state = !variant?.available ? 'sold-out' : variant.backorder ? 'backorder' : 'in-stock';
            control.dataset.stockState = state;
            const label = control.closest('label');
            if (label) label.dataset.stockState = state;
            this._stockLabel(control, label, state);
        });
        this.querySelectorAll('sticky-add-to-cart [data-current]').forEach(label => {
            label.textContent = this.variantsData[this.variantId].title || options.join(' / ');
        });
        this._syncColourSwatches();
    }

    _stockSuffix(state) {
        const label = state === 'backorder' ? this.dataset?.backorderLabel || 'Pre-order / backorder' :
            state === 'sold-out' ? this.dataset?.soldOutLabel || 'Sold out' : '';
        return label ? ` — ${label}` : '';
    }

    _stockLabel(control, label, state) {
        const suffix = this._stockSuffix(state);
        control.setAttribute?.('aria-label', `${control.value ?? control.dataset.optionValue ?? ''}${suffix}`);
        label?.querySelector('[data-option-stock-label]')?.remove();
    }

    _syncColourSwatches() {
        this.swatchImages ||= new WeakSet();
        this.querySelectorAll('[data-colour-swatch]').forEach(label => {
            const colour = label.dataset.swatchColour;
            const validColour = !!colour && CSS.supports('color', colour);
            const body = label.querySelector('.product-options-swatch-item-body');
            if (validColour) body.style.setProperty('--option-swatch-colour', colour);
            const fallback = () => { label.dataset.swatchKind = validColour ? 'colour' : 'text'; };
            const image = label.querySelector('img');
            if (!image) { fallback(); return; }
            if (image.complete && !image.naturalWidth) { fallback(); return; }
            if (!this.swatchImages.has(image)) {
                this.swatchImages.add(image);
                image.addEventListener('error', fallback, { once: true });
            }
        });
    }

    _optionState(index, value) {
        const selected = this.variantsData[this.variantId].options;
        // Sequential filtering avoids trapping the shopper in a combination:
        // first options stay browsable; later ones depend on earlier choices.
        const matches = this.variantsDataEntries.filter(([, variant]) =>
            variant.options[index] === value && variant.options.every((option, i) =>
                i >= index || option === selected[i]));
        // Do not filter matches by availability: a sold-out or backorderable
        // variant still proves that this option combination exists.
        const state = {
            exists: matches.length > 0,
            soldOut: matches.length > 0 && matches.every(([, variant]) => variant.available === false)
        };
        // Earlier options aggregate all matching variants: normal if any are
        // physically stocked, backorder only when every purchasable match is
        // continue-selling at zero/negative tracked inventory.
        if (matches.some(([, variant]) => Object.hasOwn(variant, 'backorder'))) {
            const purchasable = matches.filter(([, variant]) => variant.available);
            state.backorder = purchasable.length > 0 && purchasable.every(([, variant]) => variant.backorder);
        }
        return state;
    }

    _loadLocalFragments() {
        const store = this.querySelector('[data-local-variant-store]');
        this.localFragments = new Map();
        this.localVariantRefs = new Map();
        this.localTargetCache = new Map();
        this.localComplete = store?.dataset.complete === 'true';
        if (!store) return;
        store.querySelectorAll('template[data-local-fragment]').forEach(template => {
            this.localFragments.set(template.dataset.localFragment, template);
        });
        store.querySelectorAll('[data-local-variant-id]').forEach(script => {
            try { this.localVariantRefs.set(script.dataset.localVariantId, JSON.parse(script.textContent)); }
            catch { this.localComplete = false; }
        });
    }

    _localTargets(variantId) {
        const id = String(variantId);
        if (this.localTargetCache?.has(id)) return this.localTargetCache.get(id);
        const refs = this.localVariantRefs?.get(id);
        if (!refs) return null;
        const targets = {};
        for (const [targetId, key] of Object.entries(refs)) {
            const template = this.localFragments.get(key);
            if (!template) return null;
            const listener = [...template.content.querySelectorAll('product-variant-listener')].find(node => node.id === targetId);
            if (listener) {
                const target = listener.cloneNode(true);
                const placeholder = target.querySelector('[data-local-sticky-selector]');
                const selector = this.querySelector('sticky-add-to-cart .sticky-add-to-cart-control-selector');
                if (placeholder && selector) placeholder.replaceWith(selector.cloneNode(true));
                targets[targetId] = target;
            }
            else {
                const wrapper = document.createElement('div');
                wrapper.append(template.content.cloneNode(true));
                targets[targetId] = wrapper;
            }
        }
        this.localTargetCache.set(id, targets);
        return targets;
    }

    _canSwitchLocally(targets) {
        if (!targets || !this.localComplete || this.sellingPlanId ||
            this.querySelector('.dpo-container, #itoris_dynamicproductoptions, [data-type="add-to-cart-form"] [name^="properties["]')) return false;
        // Unknown/variant-dependent listeners must never retain stale markup.
        return [...this.querySelectorAll('product-variant-listener')].every(listener =>
            targets[listener.id] && typeof listener._onVariantUpdate === 'function');
    }

    _applyLocalPresentation(targets) {
        if (!targets || this.sellingPlanId || this.querySelector('.dpo-container, #itoris_dynamicproductoptions')) return false;
        this.querySelectorAll('product-variant-listener').forEach(listener => {
            if (targets[listener.id]) listener._onVariantUpdate?.({ targets, local: true, variantId: this.variantId });
        });
        this._syncOptionControls();
        return true;
    }

    async _handleUpdate() {
        const requestId = this.updateRequestId = (this.updateRequestId || 0) + 1;
        const variantId = this.variantId;
        const sellingPlanId = this.sellingPlanId;
        const localTargets = this._localTargets(variantId);
        const local = this._canSwitchLocally(localTargets);
        let failed = false;
        // Keep local snapshots out of the shared authoritative cache: an app
        // inserted later must not accidentally reuse a basic-form snapshot.
        const cached = local ? localTargets : Cache.get(this._cacheKey(variantId, sellingPlanId));
        this.setAttribute('aria-busy', 'true');
        // Presentation can update immediately even when an advanced form still
        // needs authoritative HTML. Purchase stays guarded until it completes.
        try {
            this.toggleAttribute('data-local-presentation', !!cached || this._applyLocalPresentation(localTargets));
            if (!cached) this._loading(true)();
            if (!cached) await this._updateCache(variantId, false, sellingPlanId);
            if (requestId !== this.updateRequestId || !this.isConnected) return;
            const targets = cached || Cache.get(this._cacheKey(variantId, sellingPlanId));
            if (!targets) throw new Error('Missing variant details');
            this._updateProps();
            this._publishUpdates(targets, local);
            this.dpo?.sync();
            this.prop('product-page') && this._updateHistory();
        } catch (error) {
            if (requestId === this.updateRequestId && this.isConnected) {
                failed = true;
                // Never purchase a new ID with old stock/price/options markup.
                const url = new URL(this.prop('product-url'), window.location.origin);
                url.search = this.historyURLParams.toString();
                window.location.assign(url.href);
            }
        } finally {
            if (requestId === this.updateRequestId && !failed) {
                this.removeAttribute('aria-busy');
                this._loading(false)();
            }
        }
    }

    _updateProps() {
        this.setProp('initial-variant-id', this.variantId);
        this.setProp('initial-image-position', this.variantImagePosition);
        this.setProp('initial-selling-plan', this.sellingPlanId);
    }

    _publishUpdates(targets = Cache.get(this._cacheKey()), local = false) {
        this.pub(Events.VARIANT_UPDATE, {
            imageIndex: this.variantImagePosition - 1,
            targets,
            local,
            variantId: this.variantId,
            imagePosition: this.variantImagePosition
        })
        // Listener replacement restores server markup; immediately reapply
        // local filtering so missing options never reappear after a response.
        this._syncOptionControls();
    }

    _initCache() {
        if (!Cache.get(this._cacheKey())) {
            Cache.set(
                this._cacheKey(),
                elementsToMap(this.$('listeners'))
            );
            return;
        }

        // if we have cache on initilization it's mean this cache was initited by someone before
        // refresh contents on the next tick
        setTimeout(() => {
            this._publishUpdates();
        }, 20)
    }

    _cacheKey(variantId, sellingPlanId) {
        return `${CACHE_KEY}-${this.sectionId}-${variantId ?? this.variantId}-${sellingPlanId ?? this.sellingPlanId}`;
    }

    async _updateCache(variantId = this.variantId, reset, sellingPlanId = this.sellingPlanId) {
        const key = this._cacheKey(variantId, sellingPlanId);
        if (!reset && Cache.get(key)) return;
        // Prefetch and selection share one request, rather than racing twice.
        if (!this.pendingRequests.has(key)) {
            const request = this._fetchListeners(variantId, sellingPlanId).then(listeners => {
                if (listeners?.length) Cache.set(key, elementsToMap(listeners));
            }).finally(() => this.pendingRequests.delete(key));
            this.pendingRequests.set(key, request);
        }
        await this.pendingRequests.get(key);
    }

    async _fetchListeners(variantId, sellingPlanId = this.sellingPlanId) {
        const url = new URL(this.prop('product-url'), window.location.origin);
        url.searchParams.set('variant', variantId || this.variantId);
        url.searchParams.set('selling_plan', sellingPlanId || '');
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Variant failed (${response.status})`);
        return Array.from(Global.Utils.parseHTML(await response.text())
            .querySelectorAll(this.elements.listeners[0]));
    }

    _canPrefetch() {
        const connection = navigator.connection;
        return !document.hidden && !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '');
    }

    _setupPrefetch() {
        this.prefetchedVariants = new Set();
        this.onVariantIntent = event => {
            if (!this._canPrefetch()) return;
            const target = event.target.closest('[data-variant-id], label');
            const input = target?.matches('[data-variant-id]') ? target : target?.querySelector('[data-variant-id]');
            const id = input?.dataset.variantId;
            if (!this.variantsData[id]) return;
            this._prefetchVariant(id).catch(() => {});
        };
        this.addEventListener('pointerover', this.onVariantIntent);
        this.addEventListener('focusin', this.onVariantIntent);
        const warmLikelyVariants = async () => {
            if (!this.isConnected || !this._canPrefetch()) return;
            const current = this.variantsData[this.variantId].options;
            const candidates = this.variantsDataEntries.filter(([id, variant]) =>
                id !== this.variantId && variant.options.filter((value, i) => value !== current[i]).length === 1
            ).slice(0, 2);
            const sellingPlanId = this.sellingPlanId;
            for (const [id] of candidates) {
                if (!this.isConnected || !this._canPrefetch() || this.hasAttribute('aria-busy')) break;
                if (sellingPlanId !== this.sellingPlanId) break;
                try { await this._prefetchVariant(id); } catch { /* Retry on selection. */ }
            }
        };
        this.prefetchTimer = setTimeout(() => {
            if (window.requestIdleCallback) {
                this.prefetchIdle = window.requestIdleCallback(warmLikelyVariants);
            } else {
                warmLikelyVariants();
            }
        }, 1500);
    }

    async _prefetchVariant(id) {
        const key = this._cacheKey(id, this.sellingPlanId);
        if (this.prefetchedVariants.has(key) || this.prefetchedVariants.size >= 6 || this.pendingRequests.size >= 2) return;
        this.prefetchedVariants.add(key);
        this._warmImage(id);
        if (this._canSwitchLocally(this._localTargets(id))) return;
        await this._updateCache(id, false, this.sellingPlanId);
    }

    _warmImage(variantId) {
        const position = this.variantsData[variantId]?.imagePosition;
        if (!position) return;
        this.querySelectorAll(`.product-gallery-media[data-index="${position - 1}"] img.media-image`).forEach(image => {
            if (image.getClientRects().length) image.loading = 'eager';
        });
    }

    _loading(state) {
        return () => {
            // Keep option controls interactive, but not purchase/express-checkout
            // controls while the selected ID's server-rendered details are stale.
            this.querySelectorAll('product-to-cart, product-dynamic-checkout').forEach(control => { control.inert = state; });
            $loading(this.$('product-meta'), state);
        }
    }

    _updateHistory() {
        // TODO: refactor to Utils
        window.history.replaceState({}, null, `${window.location.pathname}?${this.historyURLParams.toString()}`);
    }

    _onSellingPlanChange({ sellingPlanId }) {
        this.sellingPlanId = sellingPlanId;
        this._handleUpdate();
    }

    destroy() {
        this.updateRequestId = (this.updateRequestId || 0) + 1;
        clearTimeout(this.prefetchTimer);
        if (this.prefetchIdle !== undefined) window.cancelIdleCallback?.(this.prefetchIdle);
        this.removeEventListener('pointerover', this.onVariantIntent);
        this.removeEventListener('focusin', this.onVariantIntent);
        this.dpo?.destroy();
    }

    get historyURLParams() {
        const params = new URLSearchParams({
            'variant': this.variantId
        });
        this.sellingPlanId ? params.set('selling_plan', this.sellingPlanId) : params.delete('selling_plan');
        return new URLSearchParams(params);
    }
});
